import React, { useMemo, useRef, useEffect } from 'react';
import { McSelect, McLoadingIndicator, McTypeaheadMultiSelect } from '@maersk-global/mds-react-wrapper';
import { useQuery } from '@tanstack/react-query';
import { queryService } from '../../services/queryService';
import { TerminalScheduleConfig } from '../../types';
import { terminalOptions } from '../../utils/terminalConfig';

const normalizeTerminalValue = (input: string): string => input.trim().toUpperCase().replace(/_PG$/i, '');

const extractSingleSelectValue = (event: any): string => {
    const detail = event?.detail;
    if (typeof detail?.value === 'string') return detail.value;
    if (typeof detail === 'string') return detail;
    if (typeof event?.target?.value === 'string') return event.target.value;
    if (detail && typeof detail === 'object' && typeof detail.value === 'string') return detail.value;
    return '';
};

const extractMultiSelectValues = (event: any): string[] => {
    const detail = event?.detail;
    const options = Array.isArray(detail)
        ? detail
        : Array.isArray(detail?.selecteddata)
            ? detail.selecteddata
            : Array.isArray(detail?.value)
                ? detail.value
                : [];

    return options
        .map((opt: any) => {
            if (typeof opt === 'string') return opt;
            if (opt && typeof opt.value === 'string') return opt.value;
            return '';
        })
        .filter((v: string) => v.length > 0);
};

interface TerminalSelectProps {
    value: string | string[];
    onChange: (value: any) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    isMulti?: boolean;
    additionalFilter?: string[]; // optional extra allowlist, applied on top of REACT_APP_TERMINAL_IDS
}

// Map of known terminal metadata for display enrichment
const TERMINAL_METADATA: Record<string, { name: string; region: string }> = {
    'EGPSD': { name: 'Port-Said', region: 'MEA' },
    'MAPTM': { name: 'Tangiers 1', region: 'EUR' },
    'NGONN': { name: 'Onne', region: 'AFR' },
    'OMSLL': { name: 'Salalah', region: 'MEA' },
    'HRRJK': { name: 'Rijeka', region: 'EUR' },
    'BHKBS': { name: 'Bahrain', region: 'MEA' },
    'JOAQJ': { name: 'Aqaba', region: 'MEA' },
    'ESBCN': { name: 'Barcelona', region: 'EUR' },
    'ESVLC': { name: 'Valencia', region: 'EUR' },
    'SEGOT': { name: 'Gothenburg', region: 'EUR' },
    'ESALG': { name: 'Algeciras', region: 'EUR' },
    'ITVDL': { name: 'Vado', region: 'EUR' },
    'NGAPP': { name: 'Apapa', region: 'AFR' },
    'DKAAR': { name: 'Aarhus', region: 'EUR' },
    'GEPTI': { name: 'Poti', region: 'EUR' },
    'MATNG': { name: 'Tangiers 2', region: 'EUR' },
    'CRMOB': { name: 'Moin', region: 'LAM' },
    'MXLZC': { name: 'Lazaro', region: 'LAM' },
    'PECLL': { name: 'Callao', region: 'LAM' },
    'USMOB': { name: 'Mobile', region: 'NAM' },
    'GTPRQ': { name: 'Quetzal', region: 'LAM' },
    'COBUN': { name: 'Buenaventura', region: 'LAM' },
    'BRPEC': { name: 'Pecem', region: 'LAM' },
    'ARBUE': { name: 'Buenos Aires', region: 'LAM' },
    'USLAX': { name: 'Los Angeles', region: 'NAM' },
    'USPEB': { name: 'Port Elizabeth', region: 'NAM' },
    'MXPGO': { name: 'Yucatan', region: 'LAM' },
    'INPPV': { name: 'Pipavav', region: 'WCA' },
    'INNSA': { name: 'GTI Mumbai', region: 'WCA' },
};

export const TerminalSelect: React.FC<TerminalSelectProps> = ({
    value,
    onChange,
    label = "Terminal",
    placeholder = "Select terminal",
    required = false,
    disabled = false,
    className,
    style,
    isMulti = false,
    additionalFilter
}) => {
    const { data: terminals, isLoading } = useQuery({
        queryKey: ['terminalConfigs'],
        queryFn: () => queryService.listTerminalConfigs(),
        staleTime: 1000 * 60 * 60, // Cache for 1 hour
    });

    const options = useMemo(() => {
        const envAllowedIds = new Set(terminalOptions.map(t => normalizeTerminalValue(t.id)));
        const extraAllowed = additionalFilter?.length ? new Set(additionalFilter.map(normalizeTerminalValue)) : null;
        const selectedRawValues = Array.isArray(value)
            ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
            : (typeof value === 'string' && value.trim().length > 0 ? [value] : []);
        const selectedIds = new Set(selectedRawValues.map(v => normalizeTerminalValue(v)));

        // Enriched list from API data
        let finalOptions: { label: string; value: string }[] = [];

        if (terminals && terminals.length > 0) {
            finalOptions = terminals.map((t: TerminalScheduleConfig) => {
                const meta = TERMINAL_METADATA[t.terminalId] || {};
                const displayName = (t.displayName && t.displayName !== t.terminalId) ? t.displayName : (meta.name || t.terminalId);
                const region = t.region || meta.region || 'Unknown';
                return {
                    label: `${displayName} (${t.terminalId}) - ${region}`,
                    value: t.terminalId,
                };
            });
        } else {
            // Fallback to metadata if API returns nothing
            finalOptions = Object.entries(TERMINAL_METADATA).map(([id, meta]) => ({
                label: `${meta.name} (${id}) - ${meta.region}`,
                value: id
            }));
        }

        // Deduplicate options by normalized terminal ID while preserving first occurrence.
        const optionById = new Map<string, { label: string; value: string }>();
        finalOptions.forEach((opt) => {
            const normalized = normalizeTerminalValue(opt.value);
            if (!optionById.has(normalized)) {
                optionById.set(normalized, opt);
            }
        });

        // Ensure current selection remains visible even if not allowlisted.
        selectedRawValues.forEach((selectedValue) => {
            const normalized = normalizeTerminalValue(selectedValue);
            if (optionById.has(normalized)) {
                return;
            }
            const meta = TERMINAL_METADATA[normalized];
            optionById.set(normalized, meta
                ? {
                    label: `${meta.name} (${normalized}) - ${meta.region}`,
                    value: normalized,
                }
                : {
                    label: selectedValue.trim(),
                    value: selectedValue.trim(),
                });
        });

        // Filter by allowlisted IDs, while always retaining selected values.
        const filtered = Array.from(optionById.values()).filter((opt) => {
            const normalized = normalizeTerminalValue(opt.value);
            return (envAllowedIds.has(normalized) && (!extraAllowed || extraAllowed.has(normalized))) || selectedIds.has(normalized);
        });

        // Sort alphabetically by label
        return filtered.sort((a, b) => a.label.localeCompare(b.label));
    }, [terminals, value, additionalFilter]);

    const resolvedSingleValue = !isMulti && typeof value === 'string'
        ? (options.find(opt => normalizeTerminalValue(opt.value) === normalizeTerminalValue(value))?.value ?? value)
        : '';

    // McSelect is a Lit web component. When React sets `data` and `value` in
    // the same render, Lit may process `value` before the options from `data`
    // are ready in the shadow DOM, leaving the display label blank.
    // Use Lit's own `updateComplete` promise to set value AFTER the component
    // finishes its internal render cycle.
    const mcSelectRef = useRef<any>(null);
    useEffect(() => {
        const el = mcSelectRef.current;
        if (!el || !resolvedSingleValue || isMulti || options.length === 0) return;

        const syncValue = async () => {
            // Wait for Lit to finish processing the data prop
            if (el.updateComplete) await el.updateComplete;
            el.value = resolvedSingleValue;
            // Wait for the value change to take effect and update display label
            if (el.updateComplete) await el.updateComplete;
        };
        syncValue();
    }, [resolvedSingleValue, isMulti, options]);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...style }} className={className}>
                <McLoadingIndicator /> <span style={{ fontSize: '12px' }}>Loading terminals...</span>
            </div>
        )
    }

    if (isMulti) {
        const selectedValues = Array.isArray(value) ? value : [];
        const selectedSet = new Set(selectedValues.map(v => normalizeTerminalValue(v)));
        const selectedData = options.filter(opt => selectedSet.has(normalizeTerminalValue(opt.value)));

        return (
            <McTypeaheadMultiSelect
                label={label}
                placeholder={placeholder}
                data={options}
                selecteddata={selectedData}
                optionselected={(e: any) => {
                    const selected = extractMultiSelectValues(e);
                    onChange(selected);
                }}
                disabled={disabled}
                required={required}
                className={className}
                style={style}
            />
        );
    }

    return (
        <McSelect
            ref={mcSelectRef}
            label={label}
            placeholder={placeholder}
            value={resolvedSingleValue}
            data={options}
            optionselected={(e: any) => onChange(extractSingleSelectValue(e))}
            required={required}
            disabled={disabled}
            listsearch={true}
            className={className}
            style={style}
        />
    );
};
